import React, { useEffect, useMemo, useRef, useState } from 'react'
import ComplianceLabel from './ComplianceLabel'
import ComplianceTimer from './ComplianceTimer'

import { useConfigurationStore } from '@stores'

import { useOpacityToggle, useFormatMoney } from '@hooks'
import { useTranslation } from '@amusnet/translations/react'

import {
    BetMessage,
    ChangeOpacityComponents,
    GameUIMessageCommand,
    GameUIMessageScope,
    GameUIMessageType,
    SetGameNumberMessage,
    SetTotalSessionBetMessage,
    SetTotalSessionNetMessage,
    SetTotalSessionWinMessage,
    ShowMessage,
    ViewportType,
} from 'game-ui-types'
import { cn } from '@amusnet/react-ui'

const Compliance: React.FC = () => {
    const { t } = useTranslation()
    const formatMoney = useFormatMoney()
    const [totalBet, setTotalBet] = useState(0)
    const [totalWin, setTotalWin] = useState(0)
    const [totalNet, setTotalNet] = useState(0)
    const [gameNumber, setGameNumber] = useState('-1')
    const [shouldShowChanceOfMaxWin, setShouldShowChanceOfMaxWin] = useState(false)
    const [rtp, setRtp] = useState(0)

    const shouldUseUTCTime = useConfigurationStore((state) => state.shouldShowUTCTime)
    const chanceOfMaxWinProbability = useConfigurationStore((state) => state.chanceOfMaxWinProbability)
    const sendTotalsInfo = useConfigurationStore((state) => state.sendTotalsInfo)
    const skipWinAnimationsCoef = useConfigurationStore((state) => state.skipWinAnimationsCoef)
    const viewport = useConfigurationStore((state) => state.viewport)
    const complianceConfigStyles = useConfigurationStore((state) => state.getComponentConfig('Compliance').styles)

    const complianceComponent = useRef<HTMLDivElement | null>(null)
    const isNetGainEnabled = useMemo(() => {
        return (skipWinAnimationsCoef ?? -1) >= 0
    }, [skipWinAnimationsCoef])

    useOpacityToggle(ChangeOpacityComponents.COMPLIANCE, complianceComponent)

    const GameNumber = <ComplianceLabel id="compliance-game-number" value={gameNumber} />

    const TotalBet = sendTotalsInfo ? (
        <ComplianceLabel
            id="compliance-total-bet"
            label={`${t('gameui.totalBet')}:`}
            value={formatMoney(totalBet, false)}
            isMoneyLabel
        />
    ) : null

    const TotalWin = sendTotalsInfo ? (
        <ComplianceLabel
            id="compliance-total-win"
            label={`${t('gameui.totalWin')}:`}
            value={formatMoney(totalWin, false)}
            isMoneyLabel
            className="justify-end"
        />
    ) : null

    const Net = isNetGainEnabled ? (
        <ComplianceLabel id="compliance-net" label={`${t('gameui.net')}:`} value={formatMoney(totalNet, false)} />
    ) : null

    const ChanceBlock = shouldShowChanceOfMaxWin ? (
        <div className="flex flex-row">
            <ComplianceLabel id="compliance-rtp" label="RTP:" value={`${rtp}%`} className="mr-[20px]" />
            <ComplianceLabel
                id="compliance-max-win"
                label={`${t('gameui.chanceOfMaxWin')}`}
                value={chanceOfMaxWinProbability}
                className="justify-center"
            />
        </div>
    ) : null

    const Timer = <ComplianceTimer isUTC={shouldUseUTCTime} />

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const { data: rawData, type, scope } = event.data

            if (rawData?.command === GameUIMessageCommand.SHOW) {
                const data = rawData as ShowMessage
                // for local and remote games
                const initGameNumber = data.gameNumber || data?.complex?.currentState?.gameNumber || ''

                setShouldShowChanceOfMaxWin(data.shouldShowChanceOfMaxWin ?? false)
                setRtp(data.settings.rtp)
                setGameNumber(initGameNumber)
            } else if (rawData?.command === GameUIMessageCommand.BET) {
                const data = rawData as BetMessage

                setGameNumber(data.gameNumber)
            }

            if (scope === GameUIMessageScope.COMPLIANCE) {
                switch (type) {
                    case GameUIMessageType.SET_GAME_NUMBER: {
                        const data = event.data as SetGameNumberMessage

                        setGameNumber(data.value)
                        break
                    }
                    case GameUIMessageType.SET_TOTAL_SESSION_BET: {
                        const data = event.data as SetTotalSessionBetMessage

                        setTotalBet(data.value)
                        break
                    }
                    case GameUIMessageType.SET_TOTAL_SESSION_WIN: {
                        const data = event.data as SetTotalSessionWinMessage

                        setTotalWin(data.value)
                        break
                    }

                    case GameUIMessageType.SET_TOTAL_SESSION_NET: {
                        const data = event.data as SetTotalSessionNetMessage

                        setTotalNet(data.value)
                        break
                    }
                }
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    const renderPortraitLayout = () => (
        <>
            <div className="items-center flex justify-between">
                <div className="">{TotalBet}</div>
                <div className="">{TotalWin}</div>
                <div className="">{Net}</div>
            </div>

            <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                <div className="justify-self-start">{GameNumber}</div>
                <div className="justify-self-center">{ChanceBlock}</div>
                <div className="justify-self-end">{Timer}</div>
            </div>
        </>
    )

    const renderLandscapeLayout = () => (
        <div className="flex items-center justify-between">
            {GameNumber}
            {TotalBet}
            {TotalWin}
            {Net}
            {ChanceBlock}
            {Timer}
        </div>
    )

    return (
        <div
            ref={complianceComponent}
            data-compliance-bar
            className={cn('bottom-0 absolute left-0 w-full bg-[#000] z-[100] flex flex-col', complianceConfigStyles)}
        >
            {viewport === ViewportType.MOBILE_PORTRAIT ? renderPortraitLayout() : renderLandscapeLayout()}
        </div>
    )
}

export default Compliance
